// src/lib/mixpanel.ts
import type { OverridedMixpanel } from 'mixpanel-browser';
import { createDeferredSdk } from './deferredSdk';
import logger from './logger';
import { TELEMETRY_DISABLED } from './config';

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN || '';
const IS_DEV = import.meta.env.DEV;
const API_URL = import.meta.env.VITE_API_URL || '';

const sdk = createDeferredSdk<OverridedMixpanel>('Mixpanel', async () => {
  const { default: mixpanel } = await import('mixpanel-browser');

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: IS_DEV,
    track_pageview: false, // Manual control for page views
    persistence: 'localStorage',
    ignore_dnt: false, // Respect Do Not Track
  });

  // Set environment super property (included with all events)
  const env = IS_DEV ? 'development' : API_URL.includes('staging') ? 'staging' : 'production';

  mixpanel.register({
    app_environment: env,
    app_name: 'Insider Performance',
  });

  logger.log(`Mixpanel initialized for ${env} environment`);
  return mixpanel;
});

/**
 * Check if Mixpanel is configured for this environment
 */
const isMixpanelEnabled = (): boolean => {
  return Boolean(MIXPANEL_TOKEN) && !TELEMETRY_DISABLED;
};

/**
 * Load and initialize the Mixpanel SDK, then replay anything queued by withMixpanel
 */
export const loadMixpanel = (): Promise<void> => {
  if (!isMixpanelEnabled()) {
    logger.warn('Mixpanel: disabled or no token configured - analytics disabled');
    return Promise.resolve();
  }

  return sdk.load();
};

/**
 * Call the Mixpanel SDK, queueing the call until it has loaded
 */
export const withMixpanel = (action: (mixpanel: OverridedMixpanel) => void): void => {
  if (isMixpanelEnabled()) {
    sdk.run(action);
  }
};
